import classNames from 'classnames';
import {GetStaticProps, InferGetStaticPropsType} from 'next';
import {useRouter} from 'next/router';
import React, {useCallback, useMemo} from 'react';
import {SortOrder, SupplierBasic} from 'src/features/tool/marketplace/types';

import Col from '../../../features/common/Col';
import Row from '../../../features/common/Row';
import {MarketplaceCropType, MarketplaceServiceType, Point, ProductSearchParams} from '../../../features/query/graphql';
import {useIsSupplier, useOwnSupplierStatus} from '../../../features/query/supplierHooks';
import Button from '../../../features/tool/components/buttons/Button';
import {getToolLayout} from '../../../features/tool/components/Layout';
import EditYourSupplierBanner from '../../../features/tool/components/marketplace/EditYourSupplierBanner';
import ProductSearch from '../../../features/tool/components/marketplace/ProductSearch';
import SearchByCropTypeBox from '../../../features/tool/components/marketplace/SearchByCropTypeBox';
import SearchByLocationBox from '../../../features/tool/components/marketplace/SearchByLocationBox';
import SearchByServiceTypeBox from '../../../features/tool/components/marketplace/SearchByServiceTypeBox';
import SearchBySupplierBox from '../../../features/tool/components/marketplace/SearchBySupplierBox';
import SearchInput from '../../../features/tool/components/SearchInput';
import {productSearchParamsToQuery, queryToProductSearchParams} from '../../../features/tool/marketplace/util';
import {
  getApprovedSuppliers,
  getMarketplaceCropTypeList,
  getMarketplaceServiceTypeList,
} from '../../../features/tool/marketplace/util.server';
import {buildStaticPropsResponse} from '../../../features/tool/util/dataFetch';
import {Plant5LeavesIcon} from '../../../resources/Icons';
import URLS from '../../../urls';
import {NextPageWithLayout} from '../../_app';

const ProductsPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({
  suppliers,
  allCrops,
  allServices,
}) => {
  const router = useRouter();
  const isSupplier = useIsSupplier();
  const ownSupplierStatus = useOwnSupplierStatus();

  const searchParams = useMemo(() => queryToProductSearchParams(router.query), [router.query]);
  const supplierData = useMemo(
    () => suppliers.map(s => ({supplier: s, selected: !!searchParams.suppliers?.includes(s.slug)})),
    [searchParams.suppliers, suppliers]
  );

  const updateQuery = useCallback(
    (newParams: Partial<ProductSearchParams>) => {
      void router.push(
        {query: productSearchParamsToQuery({...searchParams, ...newParams, page: undefined})},
        undefined,
        {
          shallow: true,
        }
      );
    },
    [router, searchParams]
  );

  const handleChangeQuery = useCallback((value: string) => updateQuery({query: value}), [updateQuery]);

  const handleChangeProximity = useCallback(
    (postcode?: string, location?: Point, proximity?: number) => updateQuery({postcode, location, proximity}),
    [updateQuery]
  );

  const handleChangeServiceType = useCallback(
    (selected: string[]) => updateQuery({serviceTypes: selected}),
    [updateQuery]
  );

  const handleChangeCropType = useCallback((selected: string[]) => updateQuery({cropTypes: selected}), [updateQuery]);

  const handleChangeSuppliers = useCallback((selected: string[]) => updateQuery({suppliers: selected}), [updateQuery]);

  const handleSortOrderChange = useCallback(
    (order: keyof typeof SortOrder) => updateQuery({sort: order}),
    [updateQuery]
  );

  const handleTagClick = useCallback(
    (key: keyof ProductSearchParams | (keyof ProductSearchParams)[]) => {
      updateQuery(Array.isArray(key) ? key.reduce((acc, k) => ({...acc, [k]: undefined}), {}) : {[key]: undefined});
    },
    [updateQuery]
  );

  return (
    <>
      {isSupplier && <EditYourSupplierBanner status={ownSupplierStatus} />}
      <Col className="w-full max-w-wider mx-auto mt-lg mb-2xl px-sm items-center gap-md">
        <Row className={'w-full flex-wrap gap-xs items-center justify-between'}>
          <h1 className="font-semibold text-3xl leading-tight">Explore our marketplace</h1>
          <Button
            variant={'secondary'}
            accessory={<Plant5LeavesIcon className={'w-[1.4rem]'} />}
            href={URLS.marketPlace.suppliers}
          >
            Find suppliers
          </Button>
        </Row>
        <div
          className={classNames(
            'w-full grid grid-flow-col grid-cols-[33rem,1fr] grid-rows-[auto,1fr] gap-md',
            'mobile:flex mobile:flex-col mobile:gap-xs'
          )}
        >
          {/* 1st grid column */}
          <SearchInput
            placeholder={'Search'}
            onChange={handleChangeQuery}
            value={searchParams.query ?? ''}
            debounceTimeout={500}
            className={classNames(searchParams.query ? 'border-primary-700' : updateQuery)}
          />
          <Col className="flex-0 gap-xs text-sm">
            <SearchByLocationBox
              postcode={searchParams.postcode ?? undefined}
              location={searchParams.location ?? undefined}
              proximity={searchParams.proximity ?? undefined}
              onChange={handleChangeProximity}
            />
            <SearchByServiceTypeBox
              allServices={allServices}
              selectedIds={searchParams.serviceTypes ?? []}
              onChange={handleChangeServiceType}
            />
            <SearchByCropTypeBox
              allCrops={allCrops}
              selectedIds={searchParams.cropTypes ?? []}
              onChange={handleChangeCropType}
            />
            <SearchBySupplierBox supplierData={supplierData} onChange={handleChangeSuppliers} />
          </Col>
          {/* 2nd grid column */}
          <ProductSearch
            approvedSuppliers={suppliers}
            allServices={allServices}
            allCrops={allCrops}
            params={searchParams}
            onSortOrderChange={handleSortOrderChange}
            onTagClick={handleTagClick}
          />
        </div>
      </Col>
    </>
  );
};

export const getStaticProps: GetStaticProps<{
  suppliers: SupplierBasic[];
  allServices: MarketplaceServiceType[];
  allCrops: MarketplaceCropType[];
}> = async () => {
  return buildStaticPropsResponse({
    suppliers: await getApprovedSuppliers(),
    allServices: await getMarketplaceServiceTypeList(),
    allCrops: await getMarketplaceCropTypeList(),
  });
};

export default ProductsPage;

ProductsPage.getLayout = getToolLayout;

ProductsPage.title = 'Marketplace';
